import { HERO } from '../content';
import { assetPath, MEDIA_VERSION } from '../engine/tiers';
import type { SceneContext, SceneHandle } from './types';

// Re-export the shared scene types from their historical home — several
// modules import them from './hero'.
export type { SceneContext, SceneHandle } from './types';
export { scheduleIdle } from './types';

/**
 * Hero: the fox-on-the-moon title card. A 6s intro plays once (all tiers),
 * then hands off to a seamless loop — no scrubbing, no pinning. The heavy
 * fleet dioramas start at chapter 1; this keeps first paint near-instant
 * (~600KB webm / ~1.5MB mp4 vs the old 22MB frame sequence).
 */
export function mountHero(ctx: SceneContext): SceneHandle {
  const section = ctx.root.querySelector<HTMLElement>('#scene-hero');
  if (!section) throw new Error('#scene-hero missing');

  const poster = section.querySelector<HTMLImageElement>('.scene-poster');
  const video = section.querySelector<HTMLVideoElement>('.scene-video');
  if (!poster || !video) throw new Error('Hero media nodes missing');

  const posterTier = 'mobile';
  const posterUrl = assetPath(HERO.id, posterTier, 'poster');
  const endPosterUrl = posterUrl.replace(
    `${posterTier}.webp`,
    `${posterTier}-end.webp`,
  );

  poster.src = posterUrl;
  poster.alt = `${HERO.title} poster`;
  poster.setAttribute('fetchpriority', 'high');
  poster.decoding = 'async';

  const cleanups: Array<() => void> = [];

  if (ctx.reducedMotion || ctx.staticMode) {
    video.setAttribute('aria-hidden', 'true');
    video.style.visibility = 'hidden';
    if (ctx.staticMode) poster.src = endPosterUrl;
    poster.classList.add('rm-fade-in');
    return {
      section,
      video,
      scrubber: null,
      dispose: () => cleanups.forEach((fn) => fn()),
    };
  }

  video.muted = true;
  video.setAttribute('muted', '');
  video.setAttribute('playsinline', '');
  video.preload = 'auto';
  video.setAttribute('aria-hidden', 'true');

  const q = `?v=${MEDIA_VERSION}`;
  const webm = video.canPlayType('video/webm; codecs="vp9"') !== '';
  const ext = webm ? 'webm' : 'mp4';
  const introUrl = `assets/hero/intro.${ext}${q}`;
  const loopUrl = `assets/hero/loop.${ext}${q}`;

  // Mobile skips the rise: the title card opens directly on the seamless
  // loop. The intro plays on desktop only. Poster swaps to the end frame so
  // the fade lands on the loop's pose, not the intro's first frame.
  const playIntro = ctx.tier === 'desktop';
  if (!playIntro) {
    poster.src = endPosterUrl;
    video.loop = true;
  }

  let warmed: Promise<void> | null = null;
  const warmup = (): Promise<void> => {
    if (!warmed) {
      warmed = new Promise<void>((resolve) => {
        const done = (): void => resolve();
        video.addEventListener('canplaythrough', done, { once: true });
        video.addEventListener('error', done, { once: true });
        video.src = playIntro ? introUrl : loopUrl;
        video.load();
      });
    }
    return warmed;
  };

  video.addEventListener(
    'playing',
    () => poster.classList.add('is-scrubbing'),
    { once: true },
  );
  if (playIntro) {
    video.addEventListener(
      'ended',
      () => {
        video.src = loopUrl;
        video.loop = true;
        void video.play().catch(() => undefined);
      },
      { once: true },
    );
  }

  void warmup().then(() => {
    void video.play().catch(() => {
      // Autoplay denied (rare with muted): leave the poster showing.
    });
  });

  cleanups.push(() => {
    video.removeAttribute('src');
    video.load();
  });

  return {
    section,
    video,
    scrubber: null,
    warmup,
    dispose: () => cleanups.forEach((fn) => fn()),
  };
}
